/**
 * Workflow Card — the visible surface for an active multi-slot goal.
 *
 * Rendered inside `NokiCopilotPanel` between TranscriptPanel and
 * LivePartialStrip whenever a `goal.update` arrives from the server.
 * Driven entirely by the goalStore reducer — pure presentation here.
 *
 * Visual rules (matches voice_copilot_design.md §14.3):
 *
 *   slot.state                opacity   border               note
 *   --------                  -------   ------               ----
 *   empty                     30%       dashed white/15      placeholder text
 *   provisional               60%       solid white/12       governor pre-confirm
 *   confirmed                 100%      solid white/20       post-confirm
 *   locked                    100%      solid white/30       post-commit (read-only)
 *
 * Inline-styled to match the rest of NokiCopilot — no Tailwind here.
 */

import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { tokens } from "./styles";

const formatDatetime = (value) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  });
};

const formatSlotValue = (slot) => {
  if (slot.value === null || slot.value === undefined) {
    return slot.required ? "Awaiting…" : "Optional";
  }
  if (slot.type === "datetime") return formatDatetime(String(slot.value));
  if (slot.type === "boolean") return slot.value ? "Yes" : "No";
  return String(slot.value);
};

const slotOpacity = {
  empty: 0.3,
  provisional: 0.6,
  confirmed: 1,
  locked: 1,
};

const slotBorderStyle = {
  empty: { border: `1px dashed rgba(255, 255, 255, 0.15)` },
  provisional: { border: `1px solid rgba(255, 255, 255, 0.12)` },
  confirmed: { border: `1px solid rgba(255, 255, 255, 0.2)` },
  locked: {
    border: `1px solid rgba(255, 255, 255, 0.3)`,
    background: "rgba(255, 255, 255, 0.04)",
  },
};

const visibleStateForLocal = (slot, localState) => {
  if (localState === "confirming" && slot.state === "provisional") {
    return "confirmed";
  }
  return slot.state;
};

const ClassificationBadge = ({ classification }) => {
  const isRisky = classification === "WRITE_RISKY";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        border: isRisky
          ? "1px solid rgba(251, 191, 36, 0.4)"
          : "1px solid rgba(52, 211, 153, 0.3)",
        background: isRisky
          ? "rgba(251, 191, 36, 0.1)"
          : "rgba(52, 211, 153, 0.1)",
        color: isRisky ? "#FCD34D" : "#6EE7B7",
      }}
    >
      {isRisky ? "High-risk" : "Confirm to commit"}
    </span>
  );
};

const SlotRow = ({ slot, localState }) => {
  const visibleState = visibleStateForLocal(slot, localState);
  return (
    <motion.li
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        padding: "8px 12px",
        borderRadius: 12,
        opacity: slotOpacity[visibleState] ?? 1,
        transition: "opacity 0.3s ease",
        listStyle: "none",
        ...slotBorderStyle[visibleState],
      }}
    >
      <span
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: tokens.ink[300],
        }}
      >
        {slot.label}
        {slot.required ? null : (
          <span
            style={{
              marginLeft: 6,
              color: "rgba(148, 163, 184, 0.5)",
              textTransform: "none",
              letterSpacing: "normal",
            }}
          >
            optional
          </span>
        )}
      </span>
      <span
        style={{
          fontSize: 14,
          color:
            slot.value === null || slot.value === undefined
              ? "rgba(148, 163, 184, 0.6)"
              : tokens.ink[50],
          fontStyle:
            slot.value === null || slot.value === undefined ? "italic" : "normal",
        }}
      >
        {formatSlotValue(slot)}
      </span>
      {slot.description ? (
        <span style={{ fontSize: 10, color: "rgba(148, 163, 184, 0.55)" }}>
          {slot.description}
        </span>
      ) : null}
    </motion.li>
  );
};

const OutcomeStrip = ({ outcome }) => {
  const baseStyle = {
    margin: 0,
    padding: "8px 12px",
    borderRadius: 12,
    fontSize: 11,
    lineHeight: 1.4,
  };
  if (outcome.kind === "committed") {
    return (
      <div
        role="status"
        style={{
          ...baseStyle,
          border: "1px solid rgba(52, 211, 153, 0.3)",
          background: "rgba(52, 211, 153, 0.1)",
          color: "#A7F3D0",
        }}
      >
        Action committed.
      </div>
    );
  }
  if (outcome.kind === "dismissed") {
    const message =
      outcome.reason === "cancelled"
        ? "Cancelled."
        : outcome.reason === "expired"
          ? "Expired before confirmation."
          : outcome.reason === "superseded"
            ? "Superseded by another action."
            : "Dismissed.";
    return (
      <div
        role="status"
        style={{
          ...baseStyle,
          border: `1px solid ${tokens.surface.border}`,
          background: "rgba(255, 255, 255, 0.04)",
          color: tokens.ink[300],
        }}
      >
        {message}
      </div>
    );
  }
  return (
    <div
      role="alert"
      style={{
        ...baseStyle,
        border: "1px solid rgba(244, 63, 94, 0.3)",
        background: "rgba(244, 63, 94, 0.1)",
        color: "#FECACA",
      }}
    >
      {outcome.errorMessage ?? outcome.errorCode ?? "Action did not commit."}
    </div>
  );
};

const cardStyle = {
  margin: "4px 16px 12px",
  display: "flex",
  flexDirection: "column",
  gap: 12,
  padding: 16,
  borderRadius: 16,
  border: `1px solid ${tokens.surface.border}`,
  background: "rgba(255, 255, 255, 0.02)",
  boxShadow: tokens.shadow.panel,
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
};

const cancelButtonStyle = {
  borderRadius: 999,
  padding: "6px 14px",
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
  border: "1px solid rgba(255, 255, 255, 0.12)",
  background: "rgba(255, 255, 255, 0.02)",
  color: tokens.ink[200],
  transition: "border-color 0.15s ease, background 0.15s ease",
};

const confirmButtonStyle = {
  borderRadius: 999,
  padding: "6px 14px",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  border: "none",
  color: "#0F172A",
  background:
    "linear-gradient(90deg, #6EE7B7 0%, #67E8F9 100%)",
  boxShadow: "0 8px 32px -8px rgba(110, 231, 183, 0.5)",
  transition: "filter 0.15s ease",
};

const disabledStyle = { opacity: 0.55, cursor: "not-allowed" };

export function WorkflowCard({ active, outcome, onConfirm, onCancel }) {
  if (!active && !outcome) return null;

  return (
    <AnimatePresence mode="popLayout" initial={false}>
      {active ? (
        <motion.div
          key={`workflow-${active.goalId}`}
          layout
          initial={{ opacity: 0, y: 12, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.98 }}
          transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
          style={cardStyle}
          aria-live="polite"
          aria-label={`Active goal: ${active.title}`}
          data-testid="workflow-card"
          data-goal-id={active.goalId}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: tokens.ink[300],
                }}
              >
                Active goal
              </span>
              <h3
                style={{
                  margin: 0,
                  fontSize: 15,
                  fontWeight: 600,
                  color: tokens.ink[50],
                  letterSpacing: "-0.005em",
                }}
              >
                {active.title}
              </h3>
            </div>
            <ClassificationBadge classification={active.classification} />
          </div>

          {active.summary ? (
            <p
              style={{
                margin: 0,
                fontSize: 12,
                lineHeight: 1.5,
                color: tokens.ink[200],
              }}
            >
              {active.summary}
            </p>
          ) : null}

          <ul
            style={{
              margin: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: 8,
              listStyle: "none",
            }}
          >
            <AnimatePresence initial={false}>
              {(active.slots ?? []).map((slot) => (
                <SlotRow
                  key={`${active.goalId}-${slot.name}`}
                  slot={slot}
                  localState={active.localState}
                />
              ))}
            </AnimatePresence>
          </ul>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 8,
              paddingTop: 4,
            }}
          >
            <button
              type="button"
              onClick={() => onCancel(active.goalId)}
              disabled={active.localState !== "active"}
              style={{
                ...cancelButtonStyle,
                ...(active.localState !== "active" ? disabledStyle : null),
              }}
              data-testid="workflow-card-cancel"
            >
              {active.localState === "cancelling" ? "Cancelling…" : "Cancel"}
            </button>
            <button
              type="button"
              onClick={() => onConfirm(active.goalId)}
              disabled={active.localState !== "active"}
              style={{
                ...confirmButtonStyle,
                ...(active.localState !== "active" ? disabledStyle : null),
              }}
              data-testid="workflow-card-confirm"
            >
              {active.localState === "confirming" ? "Confirming…" : "Confirm"}
            </button>
          </div>
        </motion.div>
      ) : null}

      {!active && outcome ? (
        <motion.div
          key={`outcome-${outcome.goalId}`}
          layout
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
          style={{ margin: "4px 16px 8px" }}
          data-testid="workflow-card-outcome"
        >
          <OutcomeStrip outcome={outcome} />
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
